import { useEffect, useRef, useState } from "react";
import { api, type Folder, type PrivacySettings as Settings } from "../api";
import { fehlerMelden, fehlerText, toast } from "./verwaltung";

/** Was den Server verlässt — und was nicht. */
export function PrivacyPanel({ folders }: { folders: Folder[] }) {
  const [s, setS] = useState<Settings | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [zeichen, setZeichen] = useState<number | null>(null);
  const timer = useRef<number | null>(null);

  const laden = () => {
    setLadeFehler(null);
    api.privacy().then((x) => { setS(x); setZeichen(x.bodyChars); }).catch((e) => setLadeFehler(fehlerText(e)));
  };
  useEffect(laden, []);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const save = async (patch: Partial<Settings>, meldung = "Gespeichert.") => {
    setBusy(true);
    try {
      const neu = await api.savePrivacy(patch);
      setS(neu);
      setZeichen(neu.bodyChars);
      toast.erfolg(meldung);
    } catch (e) {
      fehlerMelden(e, "Nicht gespeichert");
    } finally {
      setBusy(false);
    }
  };

  // Der Regler speichert kurz nach der letzten Bewegung — mit Maus, Finger
  // oder Pfeiltasten gleichermaßen.
  const zeichenSetzen = (n: number) => {
    setZeichen(n);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void save({ bodyChars: n }, `Textauszug auf ${n.toLocaleString("de-DE")} Zeichen gesetzt.`), 600);
  };

  if (ladeFehler) {
    return (
      <div className="v-leer" data-art="fehler">
        <strong>Die Datenschutz-Einstellungen ließen sich nicht laden</strong>
        <span>{ladeFehler}</span>
        <button className="btn" onClick={laden}>Erneut versuchen</button>
      </div>
    );
  }
  if (!s) return <div className="v-laden" role="status">Lade Einstellungen …</div>;

  const byPath = new Map(folders.map((f) => [f.path, f]));
  const paths = [...new Set([...folders.map((f) => f.path), ...s.excludedFolders])].sort((a, b) => a.localeCompare(b, "de"));
  const n = zeichen ?? s.bodyChars;

  return (
    <>
      <div className="v-kopf">
        <div>
          <h2>Datenschutz</h2>
          <p className="lede">
            Zur Einordnung gehen Betreff, Absender, Empfänger und ein Textauszug jeder Mail an den gewählten
            Anbieter. Hier legst du fest, wie viel davon — und was vorher unkenntlich gemacht wird.
          </p>
        </div>
      </div>

      <div className="field">
        <label id="ds-schwaerzung">Schwärzen vor dem Senden</label>
        <div className="provider-list" role="radiogroup" aria-labelledby="ds-schwaerzung">
          {([
            ["aus", "Aus", "Der Text geht unverändert raus. Nur sinnvoll bei einem Modell auf eigener Hardware."],
            ["standard", "Standard", "IBAN, Kartennummern, Telefonnummern, Steuer-IDs und Geburtsdaten werden durch Platzhalter ersetzt. Beträge, Rechnungsnummern und Datumsangaben bleiben — die Einordnung braucht sie."],
            ["streng", "Streng", "Zusätzlich alle fremden E-Mail-Adressen und Links mit Kennung. Absender und deine eigene Adresse bleiben stehen, sonst wäre keine Einordnung möglich."],
          ] as const).map(([id, name, note]) => (
            <label key={id} className="choice" data-selected={s.redaction === id}>
              <input type="radio" name="ds-schwaerzung" checked={s.redaction === id} disabled={busy}
                onChange={() => void save({ redaction: id }, `Schwärzen: ${name}.`)} />
              <span>
                <b>{name}{id === "standard" && <span className="dsgvo dsgvo-eu">empfohlen</span>}</b>
                <span className="hint">{note}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="ds-zeichen">Wie viel Mailtext übermittelt wird</label>
        <div className="v-regler">
          <input
            id="ds-zeichen"
            type="range"
            min={200}
            max={8000}
            step={200}
            value={n}
            onChange={(e) => zeichenSetzen(Number(e.target.value))}
            aria-valuetext={`${n.toLocaleString("de-DE")} Zeichen`}
          />
          <output htmlFor="ds-zeichen">{n.toLocaleString("de-DE")} Zeichen</output>
        </div>
        <p className="hint">
          Weniger ist sparsamer und günstiger. Unter etwa 600 Zeichen leidet die Einordnung, und
          Antwortentwürfe werden spürbar schwächer.
        </p>
      </div>

      <div className="field">
        <label id="ds-ordner">Ordner, die nie an ein Modell gehen</label>
        <div className="folder-picker" role="group" aria-labelledby="ds-ordner">
          {paths.map((path) => {
            const f = byPath.get(path);
            const an = s.excludedFolders.includes(path);
            return (
              <label key={path} className="chip pick" data-on={an}>
                <input
                  type="checkbox"
                  checked={an}
                  disabled={busy}
                  onChange={(e) =>
                    void save(
                      { excludedFolders: e.target.checked ? [...s.excludedFolders, path] : s.excludedFolders.filter((p) => p !== path) },
                      e.target.checked ? `„${path}" geht nicht mehr an das Modell.` : `„${path}" wird wieder eingeordnet.`,
                    )
                  }
                />
                {f?.delimiter ? path.split(f.delimiter).join(" / ") : path}
              </label>
            );
          })}
        </div>
        <p className="hint">
          Mails in diesen Ordnern werden weder eingeordnet noch bewertet — sie verlassen den Server nicht.
        </p>
      </div>

      <label className="choice" data-selected={s.styleSamples} style={{ marginBottom: 28 }}>
        <input type="checkbox" checked={s.styleSamples} disabled={busy}
          onChange={(e) => void save({ styleSamples: e.target.checked }, e.target.checked ? "Stilvorlagen an." : "Stilvorlagen aus.")} />
        <span>
          <b>Eigene gesendete Mails als Stilvorlage mitschicken</b>
          <span className="hint">
            Macht Antwortentwürfe deutlich näher an deinem Ton — schickt dafür aber bis zu drei echte
            Mails aus deinem Gesendet-Ordner an den Anbieter. Standardmäßig aus.
          </span>
        </span>
      </label>

      <label className="choice" data-selected={s.logos} style={{ marginBottom: 28 }}>
        <input type="checkbox" checked={s.logos} disabled={busy}
          onChange={(e) => void save({ logos: e.target.checked }, e.target.checked ? "Firmenlogos an." : "Firmenlogos aus — nur noch Initialen.")} />
        <span>
          <b>Firmenlogos als Absenderbild zeigen</b>
          <span className="hint">
            Der Server fragt die Website der Absender-Domain einmal im Monat nach ihrem Symbol. Dein Browser
            kontaktiert dabei nie fremde Seiten, und welche Mail du liest, erfährt niemand. Freemailer wie
            Gmail oder GMX bekommen immer Initialen.
          </span>
        </span>
      </label>
    </>
  );
}
